import React from 'react';

export const EMPTY_SIGNAL_FILTERS = {
  blueVerified: 'any',
  canDm: 'any',
  possiblySensitive: 'any',
  hasBio: 'any',
  hasWebsite: 'any',
  hasBanner: 'any',
  hasPinned: 'any',
  hasCreatorLink: 'any',
  isIdentityVerified: 'any',
  creatorLinkKinds: [],
  tweetBuckets: [],
  likeBuckets: [],
  recencyBuckets: [],
  postStyles: [],
  langs: [],
  clients: [],
  rtCategories: [],
  usernameChangeBuckets: [],
  includeUnknownTweets: true,
  includeUnknownLikes: true,
  includeUnknownRecency: true,
  includeUnknownPostStyle: true,
  includeUnknownLang: true,
  includeUnknownClient: true,
  includeUnknownRtCategory: true,
  includeUnknownUsernameChanges: true,
  includeUnknownCreatorLink: true
};

const FLAG_ROWS = [
  { key: 'blueVerified', label: 'Blue verified' },
  { key: 'canDm', label: 'Accepts DMs' },
  { key: 'possiblySensitive', label: 'Sensitive profile' },
  { key: 'hasBio', label: 'Has bio' },
  { key: 'hasWebsite', label: 'Has website / link' },
  { key: 'hasBanner', label: 'Has banner' },
  { key: 'hasPinned', label: 'Has pinned post' },
  { key: 'hasCreatorLink', label: 'Creator link in bio' },
  { key: 'isIdentityVerified', label: 'ID verified' }
];

const TRI_OPTIONS = [
  { value: 'any', label: 'Any' },
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' }
];

const DEFAULT_TWEET_BUCKETS = [
  { key: '0', label: '0 tweets' },
  { key: '1-20', label: '1–20 tweets' },
  { key: '21-100', label: '21–100 tweets' },
  { key: '101-500', label: '101–500 tweets' },
  { key: '500+', label: '500+ tweets' }
];
const DEFAULT_LIKE_BUCKETS = [
  { key: '0-100', label: '0–100 likes' },
  { key: '101-1000', label: '101–1,000 likes' },
  { key: '1001-5000', label: '1,001–5,000 likes' },
  { key: '5000+', label: '5,000+ likes' }
];
const DEFAULT_RECENCY = [
  { key: '24h', label: 'Last 24 hours' },
  { key: '1-7d', label: '1–7 days ago' },
  { key: '7-30d', label: '7–30 days ago' },
  { key: '30d+', label: '30+ days ago' },
  { key: 'never', label: 'No tweets seen' }
];
const DEFAULT_STYLES = [
  { key: 'retweeter', label: 'Mostly retweets' },
  { key: 'original', label: 'Mostly original' },
  { key: 'replier', label: 'Mostly replies' }
];
const DEFAULT_LANGS = [
  { key: 'en', label: 'English' },
  { key: 'other', label: 'Other language' }
];
const DEFAULT_CLIENTS = [
  { key: 'iphone', label: 'iPhone' },
  { key: 'android', label: 'Android app' },
  { key: 'web', label: 'Web' }
];
const DEFAULT_RT_CATS = [
  { key: 'adult', label: 'RTs adult creators' },
  { key: 'other', label: 'RTs other creators' }
];
const DEFAULT_RENAMES = [
  { key: '0', label: 'Never renamed' },
  { key: '1', label: 'Renamed once' },
  { key: '2+', label: 'Renamed 2+' }
];
const DEFAULT_CREATOR_KINDS = [
  { key: 'onlyfans', label: 'OnlyFans' },
  { key: 'fansly', label: 'Fansly' },
  { key: 'getmysocial', label: 'GetMySocial' },
  { key: 'linktree', label: 'Linktree' },
  { key: 'beacons', label: 'Beacons' },
  { key: 'linkme', label: 'Link.me' }
];

function fmt(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return Number(n).toLocaleString();
}

function ChipGroup({ title, items, selected, onToggle, includeUnknown, onIncludeUnknown, unknownLabel }) {
  return (
    <div className="space-y-2">
      <span className="text-gray-500 uppercase tracking-wide">{title}</span>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {items.map((item) => {
          const checked = selected.includes(item.key);
          return (
            <label
              key={item.key}
              className={`flex items-center gap-2 rounded border px-2 py-1.5 cursor-pointer transition-colors ${
                checked
                  ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
            >
              <input
                type="checkbox"
                className="form-checkbox h-3.5 w-3.5"
                checked={checked}
                onChange={() => onToggle(item.key)}
              />
              <span className="flex-1 min-w-0">
                <span className="block truncate">{item.label}</span>
                <span className="block text-gray-500">{fmt(item.available)}</span>
              </span>
            </label>
          );
        })}
      </div>
      {onIncludeUnknown && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            className="form-checkbox h-3.5 w-3.5"
            checked={includeUnknown}
            onChange={(e) => onIncludeUnknown(e.target.checked)}
          />
          <span>{unknownLabel || 'Include accounts with no recorded value'}</span>
        </label>
      )}
    </div>
  );
}

function mergeItems(defaults, live) {
  if (!Array.isArray(live) || !live.length) {
    return defaults.map((item) => ({ ...item, available: null }));
  }
  const byKey = new Map(live.map((item) => [item.key, item]));
  return defaults.map((item) => ({
    ...item,
    available: byKey.get(item.key)?.available ?? null
  }));
}

export default function FollowTargetSignalFilters({ value, onChange, breakdown }) {
  const filters = { ...EMPTY_SIGNAL_FILTERS, ...(value || {}) };
  const signals = breakdown?.signals || {};

  const setField = (key, next) => onChange({ ...filters, [key]: next });
  const toggleList = (key, itemKey) => {
    const prev = Array.isArray(filters[key]) ? filters[key] : [];
    setField(
      key,
      prev.includes(itemKey) ? prev.filter((x) => x !== itemKey) : [...prev, itemKey]
    );
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <span className="text-gray-500 uppercase tracking-wide">Profile flags</span>
        <div className="space-y-1.5">
          {FLAG_ROWS.map((row) => {
            const counts = signals.flags?.[row.key] || {};
            return (
              <div key={row.key} className="flex flex-wrap items-center gap-2">
                <span className="w-40 shrink-0 text-gray-700 dark:text-gray-200">{row.label}</span>
                <div className="flex gap-1">
                  {TRI_OPTIONS.map((opt) => {
                    const active = filters[row.key] === opt.value;
                    const count = opt.value === 'yes'
                      ? counts.yes
                      : opt.value === 'no'
                        ? counts.no
                        : null;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setField(row.key, opt.value)}
                        className={`px-2 py-0.5 rounded border text-[11px] ${
                          active
                            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {opt.label}
                        {count != null && opt.value !== 'any' ? ` · ${fmt(count)}` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ChipGroup
        title="Tweets"
        items={mergeItems(DEFAULT_TWEET_BUCKETS, signals.tweetBuckets)}
        selected={filters.tweetBuckets}
        onToggle={(key) => toggleList('tweetBuckets', key)}
        includeUnknown={filters.includeUnknownTweets}
        onIncludeUnknown={(v) => setField('includeUnknownTweets', v)}
        unknownLabel="Include accounts with no recorded tweet count"
      />
      <ChipGroup
        title="Likes given"
        items={mergeItems(DEFAULT_LIKE_BUCKETS, signals.likeBuckets)}
        selected={filters.likeBuckets}
        onToggle={(key) => toggleList('likeBuckets', key)}
        includeUnknown={filters.includeUnknownLikes}
        onIncludeUnknown={(v) => setField('includeUnknownLikes', v)}
        unknownLabel="Include accounts with no recorded likes"
      />
      <ChipGroup
        title="Last active"
        items={mergeItems(DEFAULT_RECENCY, signals.recencyBuckets)}
        selected={filters.recencyBuckets}
        onToggle={(key) => toggleList('recencyBuckets', key)}
        includeUnknown={filters.includeUnknownRecency}
        onIncludeUnknown={(v) => setField('includeUnknownRecency', v)}
        unknownLabel="Include accounts not yet activity-checked"
      />
      <ChipGroup
        title="Post style"
        items={mergeItems(DEFAULT_STYLES, signals.postStyles)}
        selected={filters.postStyles}
        onToggle={(key) => toggleList('postStyles', key)}
        includeUnknown={filters.includeUnknownPostStyle}
        onIncludeUnknown={(v) => setField('includeUnknownPostStyle', v)}
      />
      <ChipGroup
        title="Language"
        items={mergeItems(DEFAULT_LANGS, signals.langs)}
        selected={filters.langs}
        onToggle={(key) => toggleList('langs', key)}
        includeUnknown={filters.includeUnknownLang}
        onIncludeUnknown={(v) => setField('includeUnknownLang', v)}
      />
      <ChipGroup
        title="Posts from"
        items={mergeItems(DEFAULT_CLIENTS, signals.clients)}
        selected={filters.clients}
        onToggle={(key) => toggleList('clients', key)}
        includeUnknown={filters.includeUnknownClient}
        onIncludeUnknown={(v) => setField('includeUnknownClient', v)}
      />
      <ChipGroup
        title="Retweet affinity"
        items={mergeItems(DEFAULT_RT_CATS, signals.rtCategories)}
        selected={filters.rtCategories}
        onToggle={(key) => toggleList('rtCategories', key)}
        includeUnknown={filters.includeUnknownRtCategory}
        onIncludeUnknown={(v) => setField('includeUnknownRtCategory', v)}
      />
      <ChipGroup
        title="Username changes"
        items={mergeItems(DEFAULT_RENAMES, signals.usernameChangeBuckets)}
        selected={filters.usernameChangeBuckets}
        onToggle={(key) => toggleList('usernameChangeBuckets', key)}
        includeUnknown={filters.includeUnknownUsernameChanges}
        onIncludeUnknown={(v) => setField('includeUnknownUsernameChanges', v)}
      />
      <ChipGroup
        title="Creator link type"
        items={mergeItems(DEFAULT_CREATOR_KINDS, signals.creatorLinkKinds)}
        selected={filters.creatorLinkKinds}
        onToggle={(key) => toggleList('creatorLinkKinds', key)}
        includeUnknown={filters.includeUnknownCreatorLink}
        onIncludeUnknown={(v) => setField('includeUnknownCreatorLink', v)}
      />
    </div>
  );
}
